import { Add } from '@mui/icons-material';
import { Autocomplete, Dialog, DialogActions, DialogContent, DialogTitle, TextField } from '@mui/material';
import React, { useEffect, useState } from 'react';
import { useAppData } from '../../../../../../context/AppDataContext';
import inventoryServices from '../../inventory/inventoryServices';
import voucherOutItemServices from '../voucherOutItemServices';
import { useAlert } from '../../../../../../context/AlertContext';
import SelectProduct from '../../product/components/SelectProduct';
import InventoryForm from '../../inventory/components/InventoryForm';
import ledgerEntryServices from '../../ledger/ledgerEntryServices';
import ProductRecentTransactionsList from './ProductRecentTransactionsList';

function VoucherOutItemForm({ voucherDetails, productList, callBack, availableLimit, isSecondary = false }) {
    const { companyLocationDD, getDDDataById } = useAppData();
    const [selectedProduct, setSelectedProduct] = useState(null);
    const { AppAlert } = useAlert();

    const [item, setItem] = useState(null);
    const [stockList, setStockList] = useState(null);
    const [errorList, setErrorList] = useState([]);


    const getStockList = () => {
        if (!item?.id) return;
        inventoryServices.getBillingStock(voucherDetails.companyId, item.id)
            .then(res => {
                setStockList(res.data);
                
                // --- AUTO-SELECT WAREHOUSE IF ONLY ONE IS AVAILABLE ---
                if (res.data && res.data.length === 1) {
                    setItem(prev => ({ ...prev, stockId: res.data[0].id }));
                }
            })
            .catch(error => {
                console.log(error);
            });
    };

    const handleItemChange = (key, value) => {
        let _item = { ...item };
        _item[key] = value === "" ? "" : value;
        setItem(_item);
    };

    // --- POPULATE INITIAL COST & PROFIT AT INITIALIZATION ---
    useEffect(() => {
        if (selectedProduct) {
            let _item = { ...selectedProduct };
            _item['qty'] = 1; 
            _item['stockId'] = 0;
            _item['isGstInclusive'] = false; 

            const initialMrp = Number(selectedProduct.mrp || selectedProduct.price || 0);
            const initialPrice = Number(selectedProduct.price || 0);
            const initialCost = Number(selectedProduct.cost || 0);
            
            _item['mrp'] = initialMrp;
            _item['price'] = initialPrice;
            _item['cost']  = initialCost; 

            // Calculate profit based on initial price and cost
            if (initialPrice > 0 && initialPrice >= initialCost) {
                const computedProfit = initialPrice - initialCost;
                _item['profitAmount'] = Number(computedProfit.toFixed(2));
                _item['profitPercent'] = initialCost > 0 ? Number(((computedProfit / initialCost) * 100).toFixed(2)) : 0;
            } else {
                _item['profitAmount'] = 0;
                _item['profitPercent'] = 0;
            }

            setItem(_item);
            setSelectedProduct(null);
        }
    }, [selectedProduct]);

    // Fetch stock list whenever the selected item changes
    useEffect(() => {
        if (item?.billingType === 1) {
            getStockList();
        }
    }, [item?.id]);

    // --- TWO-WAY INTERACTIVE PROFIT FIELD CHANGELOG ---
    const handleProfitChange = (type, val) => {
        let _item = { ...item };
        const numVal = Number(val) || 0;
        const cost = Number(_item.cost) || 0;

        if (type === 'cost') {
            _item['cost'] = val;
            const currentPct = Number(_item.profitPercent) || 0;
            const computedAmt = (numVal * currentPct) / 100;
            _item['profitAmount'] = Number(computedAmt.toFixed(2));
            _item['price'] = Number((numVal + computedAmt).toFixed(2));
        } else if (type === 'amt') {
            _item['profitAmount'] = val;
            _item['profitPercent'] = cost > 0 ? Number(((numVal / cost) * 100).toFixed(2)) : 0;
            _item['price'] = Number((cost + numVal).toFixed(2));
        } else if (type === 'pct') {
            _item['profitPercent'] = val;
            const computedAmt = (cost * numVal) / 100;
            _item['profitAmount'] = Number(computedAmt.toFixed(2));
            _item['price'] = Number((cost + computedAmt).toFixed(2));
        }
        
        setItem(_item);
    };

    // --- MANUAL PRICE INPUT BACK-TRACKS PROFIT ---
    const handlePriceDirectChange = (val) => {
        let _item = { ...item };
        _item['price'] = val;
        
        const numPrice = Number(val) || 0;
        const cost = Number(_item.cost) || 0;

        const computedAmt = numPrice - cost;
        _item['profitAmount'] = Number(computedAmt.toFixed(2));
        _item['profitPercent'] = cost > 0 ? Number(((computedAmt / cost) * 100).toFixed(2)) : 0;
        
        setItem(_item);
    };

    // --- ADJUST PROFIT COMPONENT LOGIC FOR GST TOGGLE ---
    const handleGstToggle = (isInclusive) => {
        let _item = { ...item };
        _item['isGstInclusive'] = isInclusive;

        const cost = Number(_item.cost) || 0;
        const gstRate = Number(_item.gstRate) || 0;
        let currentPrice = Number(_item.price) || 0;

        if (isInclusive) {
            const basePrice = currentPrice / (1 + (gstRate / 100));
            const computedAmt = basePrice - cost;
            _item['profitAmount'] = Number(computedAmt.toFixed(2));
            _item['profitPercent'] = cost > 0 ? Number(((computedAmt / cost) * 100).toFixed(2)) : 0;
        } else {
            const computedAmt = currentPrice - cost;
            _item['profitAmount'] = Number(computedAmt.toFixed(2));
            _item['profitPercent'] = cost > 0 ? Number(((computedAmt / cost) * 100).toFixed(2)) : 0;
        }

        setItem(_item);
    };

    // --- REAL-TIME CALCULATION BREAKDOWN ---
    const calculateValues = () => {
        if (!item) return { pricePerUnit: 0, amount: 0, gstAmount: 0, totalAmount: 0 };

        const qty = Number(item.qty) || 0;
        const enteredPrice = Number(item.price) || 0; 
        const gstRate = Number(item.gstRate) || 0;

        let pricePerUnit = enteredPrice; 
        let amount = 0;
        let gstAmount = 0;
        let totalAmount = 0;

        if (item.isGstInclusive) {
            totalAmount = enteredPrice * qty;
            amount = totalAmount / (1 + (gstRate / 100));
            gstAmount = totalAmount - amount;
            pricePerUnit = amount / (qty || 1); 
        } else {
            amount = enteredPrice * qty;
            gstAmount = (amount * gstRate) / 100;
            totalAmount = amount + gstAmount;
            pricePerUnit = enteredPrice; 
        }

        return {
            pricePerUnit: Number(pricePerUnit.toFixed(2)),
            amount: Number(amount.toFixed(2)),
            gstAmount: Number(gstAmount.toFixed(2)),
            totalAmount: Number(totalAmount.toFixed(2))
        };
    };

    const { pricePerUnit, amount, gstAmount, totalAmount } = calculateValues();

    // --- REAL-TIME VALIDATION RUNNER FOR ERRORS AND DISABLING BUTTONS ---
    const isLimitExceeded = totalAmount > (Number(availableLimit) || 0);

    const validateItemDetails = () => {
        let _errorList = [];

        if (item.billingType === 1 && (!item.stockId || item.stockId == 0)) {
            _errorList.push("No Warehouse Selected");
        }
        if (Number(item.qty) <= 0) {
            _errorList.push("Quantity cannot be 0 or less");
        }
        if (Number(item.price) < Number(item.cost)) {
            _errorList.push("Warning: Selling Price is lower than product Cost Price");
        }
        if (isLimitExceeded) {
            _errorList.push("Error: Total Net Outbound Amount exceeds the Available Limit");
        }

        return _errorList;
    };

    // Update real-time UI validation warning log whenever calculations alter totals
    useEffect(() => {
        if (item) {
            setErrorList(validateItemDetails());
        }
    }, [item, totalAmount, availableLimit]);

    const addItem = () => {
        if (validateItemDetails().length > 0) return;

        const payload = {
            ...item,
            voucherId: voucherDetails.id,
            productId: item.id,
            price: pricePerUnit, 
            amount: amount,
            gstAmount: gstAmount,
            totalAmount: totalAmount,
            profitAmount: Number((Number(item.profitAmount) * item.qty).toFixed(2)),
            profitPercent: Number(item.profitPercent),
            voucherType: voucherDetails.voucherType,
            isSecondary: isSecondary,
        };

        voucherOutItemServices.save(payload)
            .then(res => {
                AppAlert(res.msg);
                callBack();
                setItem(null);
            })
            .catch(error => {
                console.log(error);
                AppAlert(error.msg || "Error adding item", "error");
            });
    };

    return (
        <>
            <div className='row'>
                <div className='col-md-12'>
                    <SelectProduct options={productList} selectedProduct={selectedProduct} setSelectedProduct={setSelectedProduct} />
                </div>
            </div>

            <Dialog open={item !== null} keepMounted onClose={() => setItem(null)} fullWidth maxWidth="md">
                <DialogContent>
                    {item && (
                        <div className="p-2">
                            {/* --- INPUT FORM SECTION --- */}
                            <table className='table table-sm table-borderless align-middle mb-4'>
                                <tbody>
                                    <tr className={isLimitExceeded ? 'table-danger' : 'table-info'}>
                                        <th>Available Limit</th>
                                        <td colSpan={2} className="fw-bold">
                                            ₹ {availableLimit} {isLimitExceeded && <span className="text-danger ms-2"> (Limit Exceeded) </span>}
                                        </td>
                                    </tr>
                                    <tr>
                                        <th style={{ width: '30%' }}>Product Name</th>
                                        <td><strong className="text-primary fs-5">{item.title}</strong></td>
                                    </tr>
                                    <tr>
                                        <th>HSN / MRP</th>
                                        <td>
                                            <span className="badge bg-secondary px-2.5 py-1.5 me-2">HSN: {item.hsn || 'N/A'}</span>
                                            <span className="badge bg-dark px-2.5 py-1.5">MRP: ₹ {item.mrp || '0.00'}</span>
                                        </td>
                                    </tr>
                                    
                                    {/* --- COMBINED BOOTSTRAP GROUP: COST + PROFIT MARGINS --- */}
                                    {/*
                                    <tr>
                                        <th>Cost & Profit Configuration</th>
                                        <td>
                                            <div className="input-group">
                                                <span className="input-group-text bg-dark text-white small fw-bold">Cost ₹</span>
                                                <input 
                                                    className='form-control fw-bold text-danger' 
                                                    type='number' 
                                                    value={item.cost} 
                                                    onChange={(e) => handleProfitChange("cost", e.target.value)}
                                                    placeholder="0.00"
                                                />
                                                <span className="input-group-text bg-light text-secondary small">Profit + ₹</span>
                                                <input 
                                                    className='form-control text-success fw-semibold' 
                                                    type='number' 
                                                    value={item.profitAmount} 
                                                    onChange={(e) => handleProfitChange("amt", e.target.value)}
                                                    placeholder="Amt"
                                                />
                                                <span className="input-group-text bg-light text-secondary small">Margin %</span>
                                                <input 
                                                    className='form-control text-success fw-semibold' 
                                                    type='number' 
                                                    value={item.profitPercent} 
                                                    onChange={(e) => handleProfitChange("pct", e.target.value)}
                                                    placeholder="%"
                                                />
                                            </div>
                                        </td>
                                    </tr>
                                    */}

                                    {/* --- SELLING PRICE FIELD --- */}
                                    <tr>
                                        <th>Selling Price</th>
                                        <td>
                                            <div className="input-group">
                                                <span className="input-group-text bg-light fw-bold">₹</span>
                                                <input 
                                                    className='form-control form-control-lg fw-semibold' 
                                                    type='number' 
                                                    value={item.price} 
                                                    onChange={(e) => handlePriceDirectChange(e.target.value)} 
                                                    placeholder="0.00"
                                                    autoFocus
                                                />
                                                <span className="input-group-text bg-light fw-bold">GST % </span>
                                                <input className='form-control' type='number' value={item.gstRate} onChange={(e) => handleItemChange("gstRate", e.target.value)} />
                                                
                                                <div className="input-group-text bg-light">
                                                    <div className="form-check form-switch mb-0 min-height-0">
                                                        <input 
                                                            className="form-check-input me-2" 
                                                            type="checkbox" 
                                                            id="gstInclusiveCheckOut"
                                                            checked={item.isGstInclusive || false}
                                                            onChange={(e) => handleGstToggle(e.target.checked)} 
                                                            style={{ cursor: 'pointer', transform: 'scale(1.1)' }}
                                                        />
                                                        <label className="form-check-label small user-select-none fw-bold text-dark" htmlFor="gstInclusiveCheckOut" style={{ cursor: 'pointer' }}>
                                                            Inclusive GST
                                                        </label>
                                                    </div>
                                                </div>
                                            </div>
                                        </td>
                                    </tr>

                                    <tr>
                                        <td></td>
                                        <td><ProductRecentTransactionsList productId={item.id} /></td>
                                    </tr>
                                    
                                    {/*
                                    <tr>
                                        <th>GST %</th>
                                        <td>
                                            <div className="input-group" style={{ maxWidth: '200px' }}>
                                                <input className='form-control' type='number' value={item.gstRate} onChange={(e) => handleItemChange("gstRate", e.target.value)} />
                                                <span className="input-group-text">%</span>
                                            </div>
                                        </td>
                                    </tr>
                                    */}
                                    <tr>
                                        <th>Qty Out</th>
                                        <td>
                                            <input className='form-control' type='number' value={item.qty} onChange={(e) => handleItemChange("qty", e.target.value)} style={{ maxWidth: '200px' }} />
                                        </td>
                                    </tr>

                                    {item.billingType === 1 && stockList && (
                                        <tr>
                                            <th>
                                                Select Warehouse / Godown Location
                                                {/* <div className="mt-1"><InventoryForm callBack={getStockList} /></div> */}
                                            </th>
                                            <td>
                                                {stockList.length > 0 ?
                                                    <select className='form-control' value={item.stockId || 0} onChange={(e) => handleItemChange("stockId", e.target.value)}>
                                                        {stockList.length > 1 &&
                                                            <option value={0}>--- Select Warehouse ---</option>
                                                        }
                                                        
                                                        {stockList?.map(warehouse => (
                                                            <option value={warehouse.id} key={warehouse.id}>
                                                                {getDDDataById(companyLocationDD, warehouse.locationId)?.label} - [Available: {warehouse.qty}]
                                                            </option>
                                                        ))}
                                                    </select>
                                                    :
                                                    <span className='text-danger'>No Stock Available</span>
                                                }
                                            </td>
                                        </tr>
                                    )}
                                </tbody>
                            </table>

                            {/* --- SEPARATE LIVE BREAKDOWN SECTION --- */}
                            <div className="card border-0 bg-light shadow-sm">
                                <div className="card-header bg-secondary text-white fw-bold py-2 px-3 small text-uppercase tracking-wider">
                                    Outbound Invoice Breakdown Summary
                                </div>
                                <div className="card-body p-0">
                                    <table className="table table-sm mb-0 align-middle">
                                        <tbody>
                                            <tr className="border-bottom">
                                                <th className="ps-3  text-secondary fw-normal" style={{ width: '45%' }}>Item Base Price (Excl. GST)</th>
                                                <td className="pe-3 text-end fw-semibold text-dark">₹ {pricePerUnit}</td>
                                            </tr>
                                            <tr className="border-bottom">
                                                <th className="ps-3  text-secondary fw-normal">Taxable Value (Qty × Base Price)</th>
                                                <td className="pe-3 text-end fw-semibold text-dark">₹ {amount}</td>
                                            </tr>
                                            <tr className="border-bottom">
                                                <th className="ps-3  text-secondary fw-normal">GST Tax Value Added</th>
                                                <td className="pe-3 text-end fw-semibold text-danger">+ ₹ {gstAmount}</td>
                                            </tr>
                                            <tr className={isLimitExceeded ? "table-danger" : "bg-white table-active"}>
                                                <th className={`ps-3 py-0 fs-5 fw-bold ${isLimitExceeded ? 'text-danger' : 'text-primary'}`}>Total Net Outbound Amount</th>
                                                <td className={`pe-3 text-end fs-5 fw-bold ${isLimitExceeded ? 'text-danger' : 'text-primary'}`}>₹ {totalAmount}</td>
                                            </tr>
                                        </tbody>
                                    </table>
                                </div>
                            </div>

                            {/* --- ERROR MESSAGE & ACTION BUTTONS BAR --- */}
                            <div className="d-flex justify-content-between align-items-center pt-2 border-top">
                                <div className="flex-grow-1 me-3">
                                    {errorList.map((err, i) => (
                                        <div key={i} className='text-danger small fw-semibold'>• {err}</div>
                                    ))}
                                </div>
                                <button 
                                    className='btn btn-primary px-4 fw-bold' 
                                    onClick={addItem} 
                                    disabled={isLimitExceeded}
                                >
                                    Add Item
                                </button>
                            </div>
                        </div>
                    )}
                </DialogContent>
            </Dialog>
        </>
    );
}

export default VoucherOutItemForm;